/**
 * useSkillExecution Hook
 * Phase 6-4: モンスタースキル発動処理
 *
 * executeSkill: モンスターの基本技・上級技を発動
 */

import { useCallback } from 'react';
import type React from 'react';
import { executeSkillEffects } from '../../engine/effectEngine';
import { statusEffectEngine } from '../../engine/statusEffects';
import type { PlayerStatusEffect } from '../../engine/statusEffects/statusEngine';
import type { MonsterInstance, HandCard, CardWithId, CardAttribute, CardData, FieldCardInstance } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type { PendingMonsterTarget, PendingHandSelection } from '../../types/ui';

// チャージの型（CardData拡張またはSPチャージ）
interface ChargeItem {
  isSPCharge?: boolean;
  attribute?: CardAttribute;
  [key: string]: unknown;
}

interface UseSkillExecutionDeps {
  // State
  currentPlayer: PlayerNumber;
  p1Life: number;
  p2Life: number;
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];
  p1Hand: HandCard[];
  p2Hand: HandCard[];
  p1Deck: CardWithId[];
  p2Deck: CardWithId[];
  p1Graveyard: CardWithId[];
  p2Graveyard: CardWithId[];
  p1ActiveSP: number;
  p2ActiveSP: number;
  p1RestedSP: number;
  p2RestedSP: number;
  p1FieldCard: FieldCardInstance | CardData | null;
  p2FieldCard: FieldCardInstance | CardData | null;
  p1StatusEffects: PlayerStatusEffect[];
  p2StatusEffects: PlayerStatusEffect[];

  // Setters
  setP1Life: React.Dispatch<React.SetStateAction<number>>;
  setP2Life: React.Dispatch<React.SetStateAction<number>>;
  setP1Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP2Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP1Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP2Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP1Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP1Graveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2Graveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP1ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP2ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP1RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP2RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP1FieldCard: React.Dispatch<React.SetStateAction<FieldCardInstance | CardData | null>>;
  setP2FieldCard: React.Dispatch<React.SetStateAction<FieldCardInstance | CardData | null>>;
  setP1MagicBlocked: React.Dispatch<React.SetStateAction<boolean>>;
  setP2MagicBlocked: React.Dispatch<React.SetStateAction<boolean>>;
  setP1StatusEffects: React.Dispatch<React.SetStateAction<PlayerStatusEffect[]>>;
  setP2StatusEffects: React.Dispatch<React.SetStateAction<PlayerStatusEffect[]>>;
  setPendingMonsterTarget: React.Dispatch<React.SetStateAction<PendingMonsterTarget | null>>;
  setPendingHandSelection: React.Dispatch<React.SetStateAction<PendingHandSelection | null>>;

  // Utilities
  addLog: (message: string, type?: 'info' | 'damage' | 'heal') => void;
}

interface UseSkillExecutionReturn {
  executeSkill: (monsterIndex: number, skillType: 'basic' | 'advanced') => boolean;
}

/**
 * useSkillExecution Hook
 */
export const useSkillExecution = (deps: UseSkillExecutionDeps): UseSkillExecutionReturn => {
  const {
    // State
    currentPlayer,
    p1Life,
    p2Life,
    p1Field,
    p2Field,
    p1Hand,
    p2Hand,
    p1Deck,
    p2Deck,
    p1Graveyard,
    p2Graveyard,
    p1ActiveSP,
    p2ActiveSP,
    p1RestedSP,
    p2RestedSP,
    p1FieldCard,
    p2FieldCard,
    p1StatusEffects,
    p2StatusEffects,

    // Setters
    setP1Life,
    setP2Life,
    setP1Field,
    setP2Field,
    setP1Hand,
    setP2Hand,
    setP1Deck,
    setP2Deck,
    setP1Graveyard,
    setP2Graveyard,
    setP1ActiveSP,
    setP2ActiveSP,
    setP1RestedSP,
    setP2RestedSP,
    setP1FieldCard,
    setP2FieldCard,
    setP1MagicBlocked,
    setP2MagicBlocked,
    setP1StatusEffects,
    setP2StatusEffects,
    setPendingMonsterTarget,
    setPendingHandSelection,

    // Utilities
    addLog,
  } = deps;

  // =============================================================================
  // 技発動処理
  // =============================================================================

  const executeSkill = useCallback((monsterIndex: number, skillType: 'basic' | 'advanced'): boolean => {
    const field = currentPlayer === 1 ? p1Field : p2Field;
    const monster = field[monsterIndex];

    if (!monster) {
      addLog('モンスターが存在しません', 'damage');
      return false;
    }

    // 状態異常による技使用制限をチェック（眠り、雷撃、効果無効）
    if (!statusEffectEngine.canUseSkill(monster)) {
      const statusName = statusEffectEngine.getBlockingStatusName(monster, 'skill');
      addLog(`${monster.name}は${statusName}により技を使用できません！`, 'damage');
      return false;
    }

    // 1ターン1回制限チェック
    if (monster.usedSkillThisTurn) {
      addLog(`${monster.name}はこのターン既に技を発動しています`, 'damage');
      return false;
    }

    const skill = skillType === 'basic' ? monster.basicSkill : monster.advancedSkill;
    const skillName = skillType === 'basic' ? '基本技' : '上級技';

    if (!skill) {
      addLog(`このモンスターには${skillName}がありません`, 'damage');
      return false;
    }

    const requiredCharges = skill.cost;
    const currentCharges = monster.charges ? monster.charges.length : 0;

    if (currentCharges < requiredCharges) {
      addLog(`${skillName}を発動するには${requiredCharges}枚のチャージが必要です（現在: ${currentCharges}枚）`, 'damage');
      return false;
    }

    // 有効なチャージを判定（優先順位: 同属性 > なし属性 > SPチャージ）
    // SPチャージは任意属性扱いで、技発動時に消費される
    const getValidChargesForSkill = (charges: ChargeItem[], monsterAttr: CardAttribute, required: number) => {
      const sameAttr = charges.filter((c: ChargeItem) => !c.isSPCharge && c.attribute === monsterAttr);
      const noneAttr = charges.filter((c: ChargeItem) => !c.isSPCharge && c.attribute === 'なし');
      const spCharges = charges.filter((c: ChargeItem) => c.isSPCharge);

      // 優先順位順に結合して必要数だけ取得
      const orderedCharges = [...sameAttr, ...noneAttr, ...spCharges];
      const usedCharges = orderedCharges.slice(0, required);
      const spUsedCount = usedCharges.filter((c: ChargeItem) => c.isSPCharge).length;

      return {
        isValid: usedCharges.length >= required,
        usedCharges,
        spUsedCount,
      };
    };

    const chargeResult = getValidChargesForSkill(
      (monster.charges || []) as ChargeItem[],
      monster.attribute,
      requiredCharges
    );

    if (!chargeResult.isValid) {
      addLog(`${skillName}を発動するには有効なチャージが必要です（同属性、なし属性、またはSPチャージ）`, 'damage');
      return false;
    }

    // 技発動
    addLog(`${monster.name}の${skillName}を発動！`, 'info');
    addLog(`効果: ${skill.text}`, 'info');

    // 効果実行エンジンを使用
    const context = {
      currentPlayer,
      monsterIndex,
      skillType, // 'basic' or 'advanced'
      // Life
      p1Life,
      p2Life,
      setP1Life,
      setP2Life,
      // Fields
      p1Field,
      p2Field,
      setP1Field,
      setP2Field,
      // Hands
      p1Hand,
      p2Hand,
      setP1Hand,
      setP2Hand,
      // Decks
      p1Deck,
      p2Deck,
      setP1Deck,
      setP2Deck,
      // Graveyards
      p1Graveyard,
      p2Graveyard,
      setP1Graveyard,
      setP2Graveyard,
      // SP
      p1ActiveSP,
      p2ActiveSP,
      setP1ActiveSP,
      setP2ActiveSP,
      p1RestedSP,
      p2RestedSP,
      setP1RestedSP,
      setP2RestedSP,
      // Field Cards
      p1FieldCard,
      p2FieldCard,
      setP1FieldCard,
      setP2FieldCard,
      // Player Status Effects
      p1StatusEffects,
      p2StatusEffects,
      setP1StatusEffects,
      setP2StatusEffects,
      // 相手プレイヤー状態異常（カード効果用ヘルパー）
      opponentStatusEffects: currentPlayer === 1 ? p2StatusEffects : p1StatusEffects,
      setOpponentStatusEffects: currentPlayer === 1 ? setP2StatusEffects : setP1StatusEffects,
      // Utilities
      addLog,
      setPendingMonsterTarget,
      setPendingHandSelection,
      // 魔法ブロック設定（触覚持ち粘液獣等）
      setP1MagicBlocked,
      setP2MagicBlocked,
    };

    // カードIDを渡して効果を実行（カード固有処理がある場合は優先）
    const success = executeSkillEffects(skill.text, context, monster.id);

    // 技発動成功時、1ターン1回制限フラグを設定 + SPチャージを消費
    if (success !== false) {
      const setField = currentPlayer === 1 ? setP1Field : setP2Field;
      setField(prev => {
        const newField = [...prev];
        if (newField[monsterIndex]) {
          const currentMonster = newField[monsterIndex];

          // SPチャージを消費（使用した分のみ削除、属性チャージは残す）
          // chargeResult.usedChargesに含まれるSPチャージを削除
          let spRemoved = 0;
          const remainingCharges = ((currentMonster!.charges || []) as ChargeItem[]).filter((charge: ChargeItem) => {
            if (charge.isSPCharge && spRemoved < chargeResult.spUsedCount) {
              spRemoved++;
              return false; // 削除
            }
            return true; // 属性チャージは残す
          });

          newField[monsterIndex] = {
            ...currentMonster,
            usedSkillThisTurn: true,
            charges: remainingCharges as MonsterInstance['charges'],
          };
        }
        return newField;
      });

      // SPチャージ消費ログ
      if (chargeResult.spUsedCount > 0) {
        addLog(`SPチャージ${chargeResult.spUsedCount}個を消費`, 'info');
      }
    }

    return success;
  }, [currentPlayer, p1Life, p2Life, p1Field, p2Field, p1Hand, p2Hand, p1Deck, p2Deck,
      p1Graveyard, p2Graveyard, p1ActiveSP, p2ActiveSP, p1RestedSP, p2RestedSP,
      p1FieldCard, p2FieldCard, addLog, setP1Life, setP2Life, setP1Field, setP2Field,
      setP1Hand, setP2Hand, setP1Deck, setP2Deck, setP1Graveyard, setP2Graveyard,
      setP1ActiveSP, setP2ActiveSP, setP1RestedSP, setP2RestedSP, setP1FieldCard, setP2FieldCard,
      setPendingMonsterTarget, setPendingHandSelection, setP1MagicBlocked, setP2MagicBlocked]);

  return {
    executeSkill,
  };
};

export default useSkillExecution;
